<template>
    <div class="wizard-header tw-px-0 tw-py-3 lg:tw-py-6 tw-bg-brand-primary tw-text-white">
        <div
            class="wizard-header-with-dealer-selection__container tw-w-full lg:tw-container lg:tw-mx-auto tw-px-6 lg:tw-px-8 tw-flex tw-items-start lg:tw-items-center lg:tw-justify-between"
        >
            <div class="wizard-header-with-dealer-selection__wrapper_mobile lg:tw-hidden tw-w-full tw-flex tw-flex-col">
                <div class="tw-full tw-flex tw-justify-between tw-items-center">
                    <h4 class="tw-max-w-card-mobile tw-text-xl tw-leading-lg tw-font-semibold">
                        {{ make }} {{ model }}
                    </h4>
                    <div class="wizard-header-with-dealer-selection__close-btn tw-block lg:tw-self-center">
                        <dk-button
                            :href="href"
                            variant="ghost"
                            class="!tw-text-white !tw-p-0"
                            @click="openWizardExitConfirmationModal"
                        >
                            <dk-icon name="ds-close-default" size="s" />
                        </dk-button>
                    </div>
                </div>
                <div class="wizard-header-with-dealer-selection__version-mobile tw-w-full tw-flex tw-mt-1">
                    <h6
                        class="tw-flex tw-flex-row tw-flex-nowrap tw-items-center tw-text-sm tw-leading-sm tw-line-clamp-1"
                    >
                        <span class="tw-w-fit tw-font-semibold" :class="{ 'tw-mr-[2px]': activeColor }">
                            {{ versionName }}
                        </span>
                        <span v-if="showColorName" class="wizard-header__version-color"> / {{ colorName }} </span>
                    </h6>
                </div>
                <small v-if="hasDealerSelection && !selectedDealer" class="micro tw-text-blue-200">
                    {{ $t('dealer-selection.dealer-not-selected-yet') }}
                </small>
                <small v-if="hasDealerSelection && selectedDealer" class="micro tw-text-blue-200 tw-line-clamp-1">
                    <span class="tw-mr-[2px]">
                        {{ selectedDealer.name }}
                    </span>
                    <span class="wizard-header__version-color tw-capitalize">
                        - {{ selectedDealer.address.toLowerCase() }}
                    </span>
                </small>
            </div>
            <div
                class="wizard-header-with-dealer-selection__wrapper-desktop max-lg:tw-hidden tw-w-full tw-flex tw-justify-start tw-gap-4 tw-items-center"
            >
                <h4 class="tw-w-full tw-max-w-card-mobile tw-text-2xl tw-leading-lg tw-font-semibold">
                    {{ make }} {{ model }}
                </h4>
                <div
                    class="wizard-header-with-dealer-selection__version_desktop tw-w-full tw-max-w-card-mobile tw-flex tw-flex-col"
                >
                    <small class="micro tw-uppercase">
                        {{ $t('model.version.version') }}
                    </small>
                    <h6 class="tw-text-base tw-leading-base tw-font-semibold">
                        {{ versionName }}
                    </h6>
                </div>
                <div
                    class="wizard-header-with-dealer-selection__color_desktop tw-w-full tw-max-w-[216px] tw-flex tw-flex-col"
                >
                    <small class="micro tw-uppercase">
                        {{ $t('model.version.color') }}
                    </small>
                    <h6 v-if="!showColorName" class="tw-text-base tw-leading-base">
                        {{ $t('configuration-wizard.not-selected-yet') }}
                    </h6>
                    <h6
                        v-else
                        class="wizard-header__version-color tw-text-base tw-leading-base tw-font-semibold tw-line-clamp-1"
                    >
                        {{ colorName }}
                    </h6>
                </div>
                <div
                    v-if="hasDealerSelection"
                    class="wizard-header-with-dealer-selection__dealer_desktop tw-w-full tw-max-w-card-mobile tw-flex tw-flex-col"
                >
                    <small class="micro tw-uppercase">
                        {{ $t('dealer-selection.dealer') }}
                    </small>
                    <h6 v-if="!selectedDealer" class="tw-text-base tw-leading-base">
                        {{ $t('configuration-wizard.not-selected-yet') }}
                    </h6>
                    <h6 v-else class="tw-text-base tw-leading-base tw-font-semibold tw-line-clamp-1">
                        <span class="tw-mr-[2px]">
                            {{ selectedDealer.name }}
                        </span>
                        <span class="wizard-header__version-color tw-capitalize">
                            - {{ selectedDealer.address.toLowerCase() }}
                        </span>
                    </h6>
                </div>
            </div>
            <div class="wizard-header-with-dealer-selection__close-btn max-lg:tw-hidden tw-block lg:tw-self-center">
                <dk-button
                    :href="href"
                    variant="ghost"
                    class="!tw-text-white !tw-p-0"
                    @click="openWizardExitConfirmationModal"
                >
                    <dk-icon name="ds-close-default" size="s" />
                </dk-button>
            </div>
        </div>
    </div>
</template>

<script>
// mixins
import { dkModalAware } from '@v4/common/components/mixins/modal';

export default {
    name: 'WizardHeader',
    components: {
        DkIcon: () => ({
            component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
        }),
        DkButton: () => ({
            component: import(/* webpackChunkName: "DkButton-vue" */ '@v4/common/components/atoms/DkButton.vue'),
        }),
    },
    mixins: [dkModalAware],
    props: {
        /**
         * Make name
         */
        make: {
            type: String,
            default: '',
        },
        /**
         * Model name
         */
        model: {
            type: String,
            default: '',
        },
        /**
         * The selected version
         */
        selectedVersion: {
            type: Object,
            default: () => ({}),
        },
        /**
         * The "go back" link for the close button
         */
        href: {
            type: String,
            default: '',
        },
        /**
         * The current active tab
         */
        activeTab: {
            type: String,
            default: '',
        },
        hasDealerSelection: {
            type: Boolean,
            default: false,
        },
        activeColor: {
            type: Object,
            default: () => ({}),
        },
        selectedDealer: {
            type: Object,
            default: () => ({}),
        },
        selectedFakeColor: {
            type: String,
            default: '',
        },
    },
    computed: {
        versionName() {
            const version = this.selectedVersion || {};
            return version.versionName || '';
        },
        showColorName() {
            return this.activeColor || this.selectedFakeColor;
        },
        colorName() {
            return this.activeColor
                ? this.activeColor.description
                : this.selectedFakeColor
                    ? this.selectedFakeColor
                    : '';
        },
    },
    methods: {
        async openWizardExitConfirmationModal($event) {
            $event.preventDefault();

            this.$emit('click-close');

            return import(
                /* webpackChunkName: "WizardExitConfirmationModal-vue" */ '@v4/configuration-wizard/components/atoms/WizardExitConfirmationModal'
            ).then(
                async ({ default: WizardExitConfirmationModal }) =>
                    await this.showModal(
                        WizardExitConfirmationModal,
                        {
                            href: this.href,
                            activeTab: this.activeTab,
                        },
                        {
                            name: 'wizard-exit-confirmation',
                            height: 'auto',
                            width: '100%',
                            maxWidth: 448,
                            scrollable: true,
                        },
                        {},
                        {
                            disableBodyScroll: false,
                        }
                    )
            );
        },
    },
};
</script>
